import { useState } from "react";

import { Button } from "@/components/ui/button";

export const CANCEL_LABEL = "Cancel";
export const CANCELLING_LABEL = "Cancelling…";
export const CANCEL_FAILED_COPY = "Couldn’t cancel. Please try again.";
export const CANCEL_STALE_COPY =
    "Cancelled. The conversation could not be refreshed.";

/**
 * What became of a cancel, from the control's point of view.
 *
 * `refused` and `cancelled_stale` both leave this affordance on screen, so they
 * have to be told apart: one means the wait is still running and the person
 * should try again, the other means the server already ended the turn and only
 * this view is behind. Reporting the wrong one either strands the wait or
 * invites a retry of a request the server has already honoured.
 */
export type DocumentWaitCancelOutcome =
    /** The server accepted it and the thread now shows the outcome it froze. */
    | "cancelled"
    /** Accepted, but this view could not be refreshed to show it. */
    | "cancelled_stale"
    /** Nothing was cancelled: the request never went out, or it was refused. */
    | "refused";

export type DocumentWaitCancelButtonProps = {
    onCancel: () => Promise<DocumentWaitCancelOutcome>;
};

/**
 * Stops a turn parked behind processing documents.
 *
 * Ghost like the other message actions, so cancelling a wait reads as a message
 * action rather than a control the thread has no other example of.
 */
export function DocumentWaitCancelButton({
    onCancel,
}: DocumentWaitCancelButtonProps) {
    const [pending, setPending] = useState(false);
    const [outcome, setOutcome] = useState<DocumentWaitCancelOutcome | null>(
        null
    );

    const cancel = async () => {
        if (pending) return;
        setPending(true);
        setOutcome(null);
        // The caller reports every outcome it can tell apart, so a rejection
        // arriving here is one it could not classify -- and an unclassified
        // cancel is no evidence the turn ended.
        const result = await onCancel().catch(
            (): DocumentWaitCancelOutcome => "refused"
        );
        setOutcome(result);
        // Only a refusal offers the control again. An accepted abandon is
        // final, so re-enabling would invite a second one for a turn the server
        // has already settled.
        if (result === "refused") setPending(false);
    };

    return (
        <>
            {outcome === "refused" && (
                <span
                    data-slot="document-wait-cancel-error"
                    role="alert"
                    className="text-destructive text-xs"
                >
                    {CANCEL_FAILED_COPY}
                </span>
            )}
            {outcome === "cancelled_stale" && (
                <span
                    data-slot="document-wait-cancel-stale"
                    role="status"
                    className="text-muted-foreground text-xs"
                >
                    {CANCEL_STALE_COPY}
                </span>
            )}
            <Button
                data-slot="document-wait-cancel"
                type="button"
                variant="ghost"
                size="sm"
                className="text-foreground"
                disabled={pending}
                onClick={() => void cancel()}
            >
                {pending ? CANCELLING_LABEL : CANCEL_LABEL}
            </Button>
        </>
    );
}
